
import { computed, ref } from '@nuxtjs/composition-api';
import { storeToRefs } from 'pinia';
import { useChosenVariantDetailsStore } from '~/stores/useChosenVariantDetailsStore';
import useTracking from '~/composable/tracking/useTracking';
import useUtilities from '~/composable/useUtilities';
import { useVoucherPriceAdjustment } from '~/composable/voucher/useVoucherPriceAdjustment';

export default {
  setup() {
    const chosenVariantDetailsStore = useChosenVariantDetailsStore()
    const {
      chosenInstallment,
      chosenChannel,
      productDetails
    } = storeToRefs(chosenVariantDetailsStore)
    const { formatPrice } = useUtilities()
    const {
      isVoucherFeatureAvailable,
      discountCodeAmount,
      calculateAdjustedPrice,
      getPriceFromCartIfVoucherApplies,
      formatDisplayPrice
    } = useVoucherPriceAdjustment(productDetails)
    const dialog = ref(false)
    const closeDialog = () => {
      dialog.value = false
    }

    const { track } = useTracking()

    const handleClick = () => track('minimumPrice', { productDetails, chosenInstallment, chosenChannel })

    const productWithSubscriptionPrice = computed(() => chosenChannel.value?.productBundlePrice?.productWithSubscriptionPrice || 0)
    const adjustedProductWithSubscriptionPrice = computed(() => {
      const expectedDiscountedPrice = calculateAdjustedPrice(productWithSubscriptionPrice.value)
      const cartPrice = getPriceFromCartIfVoucherApplies()
      if (cartPrice === null) {
        return expectedDiscountedPrice
      }
      const isCartPriceReasonable = Math.abs(cartPrice - expectedDiscountedPrice) <= 1
      if (isCartPriceReasonable) {
        return Math.max(0, cartPrice)
      }
      return expectedDiscountedPrice
    })

    const minimumPrice = computed(() => chosenChannel.value?.productBundlePrice?.minimumPrice || 0)
    const adjustedMinimumPrice = computed(() => {
      if (!isVoucherFeatureAvailable) {
        return minimumPrice.value
      }
      return Math.max(0, minimumPrice.value - discountCodeAmount.value)
    })

    const calculateInstallmentAmountFromPrice = (lineItemPrice, months) => {
      if (!lineItemPrice || !months || months === 0) {
        return 0
      }
      return Math.ceil((lineItemPrice / months) * 100) / 100
    }

    const adjustedInstallmentAmount = computed(() => {
      const installmentMonths = chosenInstallment.value?.months || 0
      if (installmentMonths <= 0) {
        return 0
      }
      return calculateInstallmentAmountFromPrice(adjustedProductWithSubscriptionPrice.value, installmentMonths)
    })

    return {
      formatPrice,
      formatDisplayPrice,
      track,
      handleClick,
      dialog,
      closeDialog,
      productDetails,
      chosenInstallment,
      chosenChannel,
      basePrice: computed(() => chosenChannel.value?.productBundlePrice?.basePrice),
      discount: computed(() => chosenChannel.value?.productBundlePrice?.discount),
      productWithSubscriptionPrice: adjustedProductWithSubscriptionPrice,
      bundlePrice: computed(() => chosenChannel.value?.productBundlePrice?.bundlePrice),
      chosenInstallmentPeriod: computed(() => chosenInstallment.value?.months),
      bindingPeriodPayment: computed(() => chosenChannel.value?.productBundlePrice?.bindingPeriodPayment),
      minimumPrice: adjustedMinimumPrice,
      chosenInstallmentAmount: adjustedInstallmentAmount,
      signUpFeeWithBinding: computed(() => chosenChannel.value?.subscription?.signUpFeeWithBinding),
      hasDiscountPeriod: computed(() => chosenChannel.value?.subscription?.hasDiscountPeriod || false),
      discountedMonths: computed(() => {
        const days = chosenChannel.value?.subscription?.unsubscribableAfterDays || 0
        return days <= 0 ? 1 : Math.floor(days / 30)
      }),
      discountedBundlePrice: computed(() => chosenChannel.value?.subscription?.recurringDiscountPayment?.chargeValue || 0),
      normalBundlePrice: computed(() => chosenChannel.value?.subscription?.recurringPayment?.chargeValue || 0)
    }
  }
}
